import { filter, isEqual, omit } from 'lodash-es'
import { createContext, useContext, useState, useCallback, ReactNode } from 'react'
import { generateId } from './utils'

export type NotificationVariant = 'notice' | 'alert'

export interface Notification {
  message: string,
  variant: NotificationVariant,
  actionComponent?: ReactNode,
  isPersistent?: boolean,
}

export type NotificationEntry = Notification & {
  id: string
}

interface NotificationsContextProps {
  notifications: NotificationEntry[],
  addNotification: (notification : Notification) => void,
  removeNotification: (id : string) => void,
  clearNotifications: () => void
}

export const notificationsContext = createContext<NotificationsContextProps>({
  notifications: [],
  addNotification: () => { return },
  removeNotification: () => { return },
  clearNotifications: () => { return },
})

interface NotificationsProviderProps {
  initialNotifications?: Notification[],
  children: ReactNode,
}

function addUniqueId(notification: Notification): NotificationEntry {
  return {
    ...notification,
    id: `notification-${generateId()}`,
  }
}

export default function NotificationsProvider({ initialNotifications = [], children } : NotificationsProviderProps) {
  const [notifications, setNotifications] = useState<NotificationEntry[]>(() => initialNotifications.map(notification => addUniqueId(notification)))

  const removeNotification = useCallback((id: string) => {
    setNotifications(prevNotifications => filter(prevNotifications, entry => entry.id !== id))
  }, [])

  const addNotification = useCallback((notification: Notification) => {
    setNotifications(prevNotifications => {
      const filteredPrevNotifications = filter(prevNotifications, entry => !isEqual(omit(entry, 'id'), notification))
      return [...filteredPrevNotifications, addUniqueId(notification)]
    })
  }, [])

  const clearNotifications = useCallback(() => {
    setNotifications([])
  }, [])

  return (
    <notificationsContext.Provider value={{ notifications, addNotification, removeNotification, clearNotifications }}>
      {children}
    </notificationsContext.Provider>
  )
}

export function useNotifications() {
  return useContext(notificationsContext)
}
